import classNames from 'clsx'
import { arrayOf, bool, func, number, oneOf, shape, string } from 'prop-types'
import { Fragment, PureComponent } from 'react'

import { withI18n } from 'shared/utils/withI18n'
import Button from 'views/components/atoms/Button'
import IconRefresh from 'views/components/atoms/Icon/IconRefresh'
import Input from 'views/components/atoms/Input'
import Slider from 'views/components/atoms/Slider'
import { COMPONENT_SIZE, ICON_SIZE } from 'views/consts'

import './InputRange.scss'

class InputRange extends PureComponent {
  static propTypes = {
    name: string.isRequired,
    iconSize: oneOf(ICON_SIZE),
    size: oneOf(COMPONENT_SIZE),
    step: number,
    label: string,
    rounded: bool,
    placeholders: shape({
      min: string,
      max: string,
    }),
    events: shape({
      onValuesUpdated: func,
      onClick: func,
      onMinChange: func,
      onMaxChange: func,
      onSliderDragEnd: func,
      onMinEnterPress: func,
      onMaxEnterPress: func,
    }),
    maxRange: arrayOf(number),
    values: arrayOf(number),
    symbol: string,
    delimiterLabel: string,
    decimalPlaces: number,
    hideUpdateButton: bool,
    noPadding: bool,
    rheostatClassName: string,
    i18n: shape({}),
  }

  static defaultProps = {
    step: 1,
    placeholders: {
      min: '',
      max: '',
    },
    iconSize: 'small',
    size: 'small',
    rounded: true,
    label: null,
    events: {},
    maxRange: [0, 0],
    values: [0, 0],
    symbol: null,
    decimalPlaces: 2,
    delimiterLabel: null,
    hideUpdateButton: false,
    noPadding: false,
    rheostatClassName: 'u-hidden@medium',
  }

  getInputNames() {
    const { name } = this.props

    return {
      min: `${name}__min`,
      max: `${name}__max`,
      button: `${name}__button`,
      rheostat: `${name}__rheostat`,
    }
  }

  renderLabel(inputNames) {
    const { label, size } = this.props

    const labelClassNames = classNames(
      'l-flex u-font-weight--bold',
      `u-margin-${size}--bottom`,
      {
        'a-h3': size === 'large',
        'a-h4': size === 'default',
        'a-h5': size === 'small',
      }
    )

    return (
      <label className={labelClassNames} htmlFor={inputNames.min}>
        {label}
      </label>
    )
  }

  getRheostatValues = () => {
    const {
      values: [min, max],
      maxRange: [rangeMin, rangeMax],
    } = this.props

    return [min >= rangeMin ? min : rangeMin, max <= rangeMax ? max : rangeMax]
  }

  render() {
    const {
      step,
      rounded,
      label,
      size,
      iconSize,
      placeholders,
      delimiterLabel,
      events: {
        onValuesUpdated,
        onClick,
        onMinChange,
        onMaxChange,
        onSliderDragEnd,
        // TODO rename to onKeyUp
        onMinEnterPress,
        onMaxEnterPress,
      },
      values: [min, max],
      maxRange: [rangeMin, rangeMax],
      symbol,
      hideUpdateButton,
      rheostatClassName,
      noPadding,
      i18n,
    } = this.props
    const inputNames = this.getInputNames()
    const inputClassNames = classNames(
      'm-InputRange__input u-font-weight--normal u-no-margin u-border',
      `u-padding-${size}`,
      {
        'u-border-radius': rounded,
        'm-InputRange__input--with-symbol': symbol,
      }
    )

    let WrapperComponent
    let wrapperProps
    let labelRendered

    if (label) {
      WrapperComponent = Fragment
      wrapperProps = {}
      labelRendered = this.renderLabel(inputNames)
    } else {
      WrapperComponent = 'label'
      wrapperProps = { htmlFor: inputNames.min }
      labelRendered = ''
    }

    const symbolComponent = symbol ? (
      <span className="m-InputRange__symbol u-position--absolute u-transform-center--y">
        {symbol}
      </span>
    ) : null

    return (
      <WrapperComponent {...wrapperProps}>
        {labelRendered}
        <div className="l-flex l-flex--stretch-children u-margin-x-large--bottom@small-tiny m-InputRange__wrapper">
          <div className="u-margin--right l-flex__primary u-position--relative">
            <Input
              name={inputNames.min}
              id={inputNames.min}
              className={inputClassNames}
              type="number"
              step={step}
              placeholder={placeholders.min}
              defaultValue={min}
              disableAutoCompleteAndFriends
              onBlur={onMinChange}
              onKeyUp={onMinEnterPress}
            />
            {symbolComponent}
          </div>
          {delimiterLabel && (
            <div className="m-InputRange__delimiter l-flex__center u-color--grey">
              {delimiterLabel}
            </div>
          )}
          <div
            className={classNames('l-flex__primary u-position--relative', {
              'u-margin-small--right@medium': !noPadding,
            })}
          >
            <Input
              name={inputNames.max}
              id={inputNames.max}
              className={inputClassNames}
              type="number"
              step={step}
              placeholder={placeholders.max}
              defaultValue={max}
              disableAutoCompleteAndFriends
              onBlur={onMaxChange}
              onKeyUp={onMaxEnterPress}
            />
            {symbolComponent}
          </div>
          {hideUpdateButton ? null : (
            <Button
              name={inputNames.button}
              className="m-InputRange__button u-hidden@small-tiny"
              aria-label={i18n._('filter.input-range.refresh-button.aria')}
              onClick={e => {
                e.preventDefault()
                onClick()
              }}
              rounded={rounded}
              inline
              reverse
            >
              <IconRefresh size={iconSize} aria-hidden />
            </Button>
          )}
        </div>
        <div
          className={classNames('m-InputRange__rheostat', rheostatClassName)}
        >
          <Slider
            max={rangeMax}
            min={rangeMin}
            onChange={(event, values) => onValuesUpdated({ values })}
            onChangeCommitted={onSliderDragEnd}
            step={0.01}
            value={this.getRheostatValues()}
          />
        </div>
      </WrapperComponent>
    )
  }
}

export default withI18n()(InputRange)
